// POS → Configuración → Balanza / Etiqueta de peso (/pos/balanza-etiqueta, 706).
// Define el formato del código de barras de PESO VARIABLE que imprime la
// balanza etiquetadora de la empresa: prefijos, posición del PLU y del valor,
// peso (gramos) o precio (centavos), y validación del dígito verificador.
// Incluye un PROBADOR en vivo: pega un código de una etiqueta real y muestra
// cómo lo interpretaría el punto de venta con la configuración del formulario.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  BALANZA_ETIQUETA_DEFAULT, BalanzaEtiquetaCfg, Campo,
  balanzaEtiquetaGet, balanzaEtiquetaSet, candidatosPlu, fieldCls, parsearPesoVariable,
} from './shared';

export function ConfiguracionBalanza() {
  const qc = useQueryClient();
  const [form, setForm] = useState<BalanzaEtiquetaCfg | null>(null);
  const [prueba, setPrueba] = useState('');

  const { data: cfg, isLoading } = useQuery<BalanzaEtiquetaCfg>({
    queryKey: ['pos-balanza-etiqueta'], queryFn: balanzaEtiquetaGet, staleTime: 0, refetchOnMount: 'always',
  });
  const vigente = form ?? cfg ?? BALANZA_ETIQUETA_DEFAULT;
  const set = (patch: Partial<BalanzaEtiquetaCfg>) => setForm({ ...vigente, ...patch });

  const guardar = useMutation({
    mutationFn: () => balanzaEtiquetaSet(vigente),
    onSuccess: () => {
      toast.success('Formato de etiqueta guardado');
      setForm(null);
      void qc.invalidateQueries({ queryKey: ['pos-balanza-etiqueta'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Probador en vivo con la config DEL FORMULARIO (aún sin guardar)
  const resultado = useMemo(() => {
    const c = prueba.trim();
    if (!c) return null;
    if (!/^\d{13}$/.test(c)) return { error: 'El código debe tener exactamente 13 dígitos' };
    const pv = parsearPesoVariable(c, vigente);
    if (!pv) return { error: 'No coincide con el formato configurado (prefijo, verificador o valor en cero) — se trataría como código normal' };
    return { pv };
  }, [prueba, vigente]);

  const num = (k: keyof BalanzaEtiquetaCfg) => (
    <input type="number" min={3} max={12} className={`${fieldCls} !w-20`} value={String(vigente[k])}
           onChange={(e) => set({ [k]: Number(e.target.value) || 0 } as Partial<BalanzaEtiquetaCfg>)} />
  );

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="p-6 max-w-3xl space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Scale className="w-5 h-5" /> Balanza / Etiqueta de peso</h1>
      <p className="text-sm text-muted-foreground">
        Formato del código de barras de <span className="font-medium">peso variable</span> (EAN-13) que imprime la balanza
        etiquetadora. Al escanearlo, el punto de venta busca el producto por el <span className="font-medium">PLU</span> y usa el
        valor embebido como <span className="font-medium">cantidad en kg</span> (o la deriva del precio). El código del producto
        en el catálogo debe coincidir con el PLU de la balanza.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Formato</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={vigente.activo} onChange={(e) => set({ activo: e.target.checked })} />
            Interpretar códigos de peso variable al escanear
          </label>
          <div className="flex flex-wrap items-end gap-4">
            <Campo label="Prefijos (separados por coma)">
              <input className={`${fieldCls} !w-36 font-mono`} value={vigente.prefijos}
                     onChange={(e) => set({ prefijos: e.target.value })} placeholder="20,21" />
            </Campo>
            <Campo label="PLU desde (dígito)">{num('plu_desde')}</Campo>
            <Campo label="PLU longitud">{num('plu_longitud')}</Campo>
            <Campo label="Valor desde (dígito)">{num('valor_desde')}</Campo>
            <Campo label="Valor longitud">{num('valor_longitud')}</Campo>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <Campo label="El valor embebido es…">
              <select className={`${fieldCls} !w-64`} value={vigente.valor_tipo}
                      onChange={(e) => set({ valor_tipo: e.target.value as BalanzaEtiquetaCfg['valor_tipo'] })}>
                <option value="peso_gramos">PESO en gramos (cantidad = valor / 1000 kg)</option>
                <option value="precio">PRECIO en centavos (cantidad = importe / PVP)</option>
              </select>
            </Campo>
            <label className="flex items-center gap-2 text-sm pb-2">
              <input type="checkbox" checked={vigente.validar_dv} onChange={(e) => set({ validar_dv: e.target.checked })} />
              Validar dígito verificador (recomendado)
            </label>
          </div>
          <div className="flex justify-end">
            <Button isLoading={guardar.isPending} disabled={!form} onClick={() => guardar.mutate()}>
              <Save className="w-4 h-4 mr-1" /> Guardar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Probador — escanea o pega un código de una etiqueta real</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <input className={`${fieldCls} font-mono`} placeholder="ej. 2012345014206" maxLength={13}
                 value={prueba} onChange={(e) => setPrueba(e.target.value.replace(/\D/g, ''))} />
          {resultado && ('error' in resultado ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              {resultado.error}
            </div>
          ) : (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm dark:bg-emerald-900/15 space-y-1">
              <div>✔ Etiqueta de peso variable reconocida</div>
              <div>PLU: <span className="font-mono font-medium">{resultado.pv!.plu}</span>
                {' '}(el POS buscará el producto con código {candidatosPlu(resultado.pv!.plu).map((c) => <span key={c} className="font-mono">"{c}" </span>)})</div>
              {resultado.pv!.pesoKg != null && <div>Cantidad: <span className="font-mono font-medium">{resultado.pv!.pesoKg.toFixed(3)} kg</span></div>}
              {resultado.pv!.importe != null && <div>Importe: <span className="font-mono font-medium">${resultado.pv!.importe.toFixed(2)}</span> (cantidad = importe ÷ PVP del producto)</div>}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            El probador usa la configuración del formulario tal como está (aunque no la hayas guardado) —
            ideal para calibrar contra una etiqueta impresa antes de aplicar.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
